import { FC } from "react";

import { Grid } from "@mui/material";
import { FormProvider, UseFormReturn } from "react-hook-form";

import { useLocaleContext } from "../../../../../common/contexts/LocaleContext";
import { isMax, isMin } from "../../../../../common/helpers/validators";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { FormStepContainer } from "../FormStepContainer";

import { StepFundraisingGoalFormData } from "./types";

import { DeleteConfirmations } from "@/components/DeleteConfirmation/DeleteConfirmation";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Add as AddIcon } from "@/icons/outlined";

interface LiveEditorStepFundraisingGoalStatelessProps {
  form: UseFormReturn<StepFundraisingGoalFormData>;
  title: string;
  description: string;
  addButtonLabel: string;
  handleSaveAndBack: () => void;
  handleSaveAndNext: () => void;
  target: number | null;
  showTargetInput: boolean;
  onAddFundraisingGoal?: () => void;
  onRemoveFundraisingGoal?: () => void;
  hadInitialTarget: boolean;
  isGoalMandatory?: boolean;
}

export const LiveEditorStepFundraisingGoalStateless: FC<LiveEditorStepFundraisingGoalStatelessProps> = ({
  form,
  title,
  description,
  addButtonLabel,
  handleSaveAndBack,
  handleSaveAndNext,
  target,
  showTargetInput,
  onAddFundraisingGoal,
  onRemoveFundraisingGoal,
  hadInitialTarget,
  isGoalMandatory,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  const { formCurrency } = useLiveFormEditorContext();

  const amountMax = 1_000_000_000;
  const { errors } = form.formState;

  const onAddFundraisingGoalClick = () => {
    onAddFundraisingGoal?.();
    form.setValue("target", 100000, {
      shouldDirty: true,
      shouldTouch: true,
    });
    form.setValue("showInput", true, {
      shouldDirty: true,
    });
  };

  const handleDeleteTarget = () => {
    onRemoveFundraisingGoal?.();
    form.setValue("target", null, { shouldDirty: true });
    form.setValue("showInput", false, { shouldDirty: true });
  };

  return (
    <FormProvider {...form}>
      <FormStepContainer
        title={title}
        description={description}
        form={form}
        actionsProps={{
          onBack: handleSaveAndBack,
          onNext: handleSaveAndNext,
          nextButtonLabel: target || hadInitialTarget ? "next" : "skip",
        }}
      >
        {!showTargetInput && (
          <DashboardButton
            data-test="add-fundraising-goal-button"
            startIcon={<AddIcon />}
            variant="outlined"
            vibe="brand"
            onClick={onAddFundraisingGoalClick}
          >
            {addButtonLabel}
          </DashboardButton>
        )}
        {showTargetInput && (
          <LiveEditorBlock>
            <Grid container alignItems="center" sx={{ marginBottom: 1 }}>
              <Grid item xs={11}>
                <DashboardTypography variant="subtitle2">{t("dashboard", "donationForm.target")}</DashboardTypography>
              </Grid>
              <Grid item xs={1}>
                {!isGoalMandatory && <DeleteConfirmations confirmation={handleDeleteTarget} shouldConfirm={false} />}
              </Grid>
            </Grid>
            <DashboardAmountInput
              control={form.control}
              name="target"
              type="number"
              data-test="donation-form-target-input"
              locale={locale}
              currency={formCurrency}
              noAsterisk
              errorMessage={errors.target?.message}
              rules={[
                isMin(100, t("donationForm", "targetMin")),
                isMax(amountMax, t("dashboard", "common.isMax", { max: amountMax / 100 })),
              ]}
            />
          </LiveEditorBlock>
        )}
      </FormStepContainer>
    </FormProvider>
  );
};
